<template>
  <div class="app-calendar overflow-hidden border">
    <div class="row no-gutters">
      <!-- Sidebar -->
      <div
        class="col app-calendar-sidebar flex-grow-0 overflow-hidden d-flex flex-column"
        :class="{ show: sidebarOverlayActive }"
      >
        <div class="sidebar-wrapper d-flex flex-column flex-grow-1">
          <div class="sidebar-header px-2 pt-2 pb-1">
            <div class="sidebar-page-title mb-1">
              <feather-icon
                icon="CalendarIcon"
                size="20"
                class="text-primary mr-50"
              />
              <span>{{ $t("Timetable") }}</span>
            </div>
            <!-- <p class="sidebar-page-hint mb-0">
              {{ $t("Select staff, then click a date on the calendar") }}
            </p> -->
          </div>

          <div class="sidebar-body px-2 pb-2 flex-grow-1 overflow-auto">
            <!-- Staff -->
            <!-- <div class="sidebar-card mb-2">
              <div class="sidebar-card-label">{{ $t("Staff") }}</div>
              <v-select
                v-model="selectedStaffId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="staffOptions"
                :reduce="(opt) => opt.id"
                label="label"
                :clearable="false"
                :loading="staffLoading"
                :disabled="staffLoading"
                :placeholder="$t('Search staff...')"
                class="staff-vselect"
              >
                <template #option="option">
                  <div class="staff-option">
                    <span class="staff-option-name">{{ option.name }}</span>
                    <small v-if="option.designation" class="text-muted d-block">
                      {{ option.designation }}
                    </small>
                  </div>
                </template>
                <template #selected-option="option">
                  <span>{{ option.name }}</span>
                </template>
              </v-select>

              <div v-if="selectedStaff" class="staff-chip mt-1">
                <div class="staff-chip-avatar">
                  <feather-icon icon="UserCheckIcon" size="16" />
                </div>
                <div>
                  <div class="staff-chip-name">{{ selectedStaff.name }}</div>
                  <div class="staff-chip-meta">
                    {{ selectedStaff.designation || $t("Staff Member") }}
                  </div>
                </div>
              </div>
            </div> -->

            <!-- Selected day -->
            <div v-if="selectedDate" class="sidebar-card sidebar-card-day mb-2">
              <div class="sidebar-card-label">{{ $t("Selected Day") }}</div>
              <div class="selected-day-title">
                {{ formatDisplayDate(selectedDate) }}
              </div>
              <!-- <div class="selected-day-sub">
                {{ weekdayLabel(selectedWeekday) }}
              </div> -->
            </div>

            <!-- No staff -->
            <!-- <div v-if="!selectedStaffId" class="sidebar-empty">
              <feather-icon
                icon="UsersIcon"
                size="28"
                class="text-muted mb-50"
              />
              <span>{{ $t("Select a staff member to view timetable.") }}</span>
            </div> -->

            <!-- Loading -->
            <div v-if="loading" class="sidebar-empty">
              <b-spinner small variant="primary" class="mb-50" />
              <span>{{ $t("Loading timetable...") }}</span>
            </div>

            <!-- No date yet -->
            <div v-else-if="!selectedDate" class="sidebar-empty">
              <feather-icon
                icon="MousePointerIcon"
                size="28"
                class="text-muted mb-50"
              />
              <span>{{ $t("Click a date on the calendar") }}</span>
            </div>

            <!-- Day details + class list -->
            <div v-else class="sidebar-details-panel">
              <div class="sidebar-card-label classes-heading mb-1">
                <span>
                  {{ $t("Classes") }}
                  <feather-icon
                    v-if="rights.add"
                    icon="PlusCircleIcon"
                    size="20"
                    class="text-primary ml-50 cursor-pointer"
                    v-b-tooltip.hover.top
                    :title="$t('Add Class')"
                    @click="openAddForm()"
                  />
                </span>
                <span v-if="daySessions.length" class="classes-count">
                  {{ daySessions.length }}
                </span>
              </div>

              <div
                v-if="!daySessions.length"
                class="sidebar-empty sidebar-empty-compact mb-2"
              >
                <feather-icon
                  icon="BookOpenIcon"
                  size="24"
                  class="text-muted mb-50"
                />
                <span>{{ $t("No classes scheduled for this day.") }}</span>
              </div>

              <div v-else class="session-list">
                <div
                  v-for="session in daySessions"
                  :key="sessionKey(session)"
                  class="session-card"
                  :class="{
                    active: activeSessionKey === sessionKey(session),
                    'session-card--readonly': !rights.edit,
                  }"
                  :data-session-key="sessionKey(session)"
                  @click="onSessionClick(session)"
                >
                  <div class="session-card-main">
                    <div class="session-card-time">
                      {{ sessionDisplayTime(session) }}
                    </div>
                    <div class="session-card-title">
                      {{ sessionDisplayTitle(session) }}
                    </div>
                    <div class="session-card-meta">
                      {{ sessionDisplayMeta(session) }}
                    </div>
                    <div
                      v-if="
                        isHappiness &&
                        (session.ageCategory || session.empowerLevel)
                      "
                      class="session-tags"
                    >
                      <span
                        v-if="session.ageCategory"
                        class="session-tag text-capitalize"
                      >
                        {{ session.ageCategory }}
                      </span>
                      <span
                        v-if="session.empowerLevel"
                        class="session-tag text-capitalize"
                      >
                        {{ session.empowerLevel }}
                      </span>
                    </div>
                  </div>
                  <div
                    v-if="rights.edit || rights.delete"
                    class="session-card-actions"
                    @click.stop
                  >
                    <b-button
                      size="sm"
                      variant="flat-info"
                      class="btn-icon"
                      v-b-tooltip.hover.top
                      :title="$t('Send Message')"
                      @click="openMessageModal(session)"
                    >
                      <feather-icon icon="MessageSquareIcon" size="15" />
                    </b-button>
                    <b-button
                      v-if="rights.edit"
                      size="sm"
                      variant="flat-primary"
                      class="btn-icon"
                      v-b-tooltip.hover.top
                      :title="$t('Copy')"
                      @click="openCopyForm(session)"
                    >
                      <feather-icon icon="CopyIcon" size="15" />
                    </b-button>
                    <b-button
                      v-if="rights.edit"
                      size="sm"
                      variant="flat-primary"
                      class="btn-icon"
                      v-b-tooltip.hover.top
                      :title="$t('Edit')"
                      @click="openEditForm(session)"
                    >
                      <feather-icon icon="EditIcon" size="15" />
                    </b-button>
                    <b-button
                      v-if="rights.delete"
                      size="sm"
                      variant="flat-danger"
                      class="btn-icon"
                      v-b-tooltip.hover.top
                      :title="$t('Delete')"
                      @click="deleteEntry(session)"
                    >
                      <feather-icon icon="Trash2Icon" size="15" />
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Calendar -->
      <div class="col position-relative">
        <div class="card shadow-none border-0 mb-0 rounded-0 overflow-hidden">
          <transition name="fade">
            <div v-if="loading" class="calendar-skeleton-overlay">
              <div
                class="skeleton-header d-flex justify-content-between mb-3 px-2 pt-2"
              >
                <div class="skeleton-title-box"></div>
                <div class="skeleton-nav-box d-flex">
                  <div class="skeleton-btn mr-1"></div>
                  <div class="skeleton-btn"></div>
                </div>
              </div>
              <div class="skeleton-grid px-2 pb-2">
                <div class="skeleton-row-header d-flex mb-2">
                  <div
                    v-for="d in 7"
                    :key="'sh-' + d"
                    class="skeleton-cell-header"
                  ></div>
                </div>
                <div
                  v-for="r in 6"
                  :key="'sr-' + r"
                  class="skeleton-row d-flex"
                >
                  <div
                    v-for="c in 7"
                    :key="'sc-' + r + '-' + c"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-date"></div>
                    <div
                      v-if="c % 3 == 0"
                      class="skeleton-event-bar mb-1"
                    ></div>
                    <div
                      v-if="c % 2 == 0"
                      class="skeleton-event-bar-small"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </transition>

          <div
            class="card-body pb-0"
            :class="{ 'calendar-loading-blur': loading }"
          >
            <full-calendar
              ref="calendar"
              :options="calendarOptions"
              class="full-calendar"
            >
              <template v-slot:eventContent="arg">
                <div
                  class="event-content tt-event-block"
                  :class="`tt-event-tone-${
                    arg.event.extendedProps.toneIndex || 0
                  }`"
                >
                  <small class="event-time-badge">
                    {{ arg.event.extendedProps.timeLabel || "" }}
                  </small>
                  <b class="event-title" :title="arg.event.title">
                    {{ truncate(arg.event.title, 36) }}
                  </b>
                  <small
                    v-if="arg.event.extendedProps.subtitle"
                    class="event-meta-line"
                  >
                    {{ truncate(arg.event.extendedProps.subtitle, 32) }}
                  </small>
                </div>
              </template>
            </full-calendar>
          </div>
        </div>
      </div>
    </div>

    <div
      class="body-content-overlay"
      :class="{ show: sidebarOverlayActive }"
      @click="sidebarOverlayActive = false"
    />

    <!-- Right form sidebar (event-calendar style) -->
    <b-sidebar
      id="timetable-form-sidebar"
      v-model="formSidebarVisible"
      bg-variant="white"
      width="600px"
      shadow
      backdrop
      no-header
      right
      sidebar-class="timetable-form-sidebar"
    >
      <div class="sidebar-header-modern">
        <div class="sidebar-header-content">
          <div class="sidebar-title-wrapper">
            <div class="sidebar-title-icon-box">
              <feather-icon
                :icon="sidebarFormMode === 'edit' ? 'EditIcon' : 'PlusIcon'"
                size="20"
              />
            </div>
            <h5 class="sidebar-title">
              {{
                sidebarFormMode === "edit"
                  ? $t("Edit Class Details")
                  : $t("Add Class")
              }}
            </h5>
          </div>
          <div class="header-actions d-flex align-items-center">
            <b-button
              v-if="rights.delete && sidebarFormMode === 'edit' && timetable.id"
              variant="outline-danger"
              size="sm"
              class="sidebar-delete-btn mr-1"
              v-b-tooltip.hover.bottom="$t('Delete')"
              @click="deleteCurrentEntry()"
            >
              <feather-icon icon="Trash2Icon" size="18" />
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="sidebar-close-btn"
              @click="closeFormPanel()"
            >
              <feather-icon icon="XIcon" size="20" />
            </b-button>
          </div>
        </div>
      </div>

      <div class="sidebar-body-modern">
        <div v-if="selectedDate" class="form-day-banner mb-2">
          <feather-icon
            icon="CalendarIcon"
            size="16"
            class="mr-50 text-primary"
          />
          <div>
            <div class="form-day-title">
              {{ formatDisplayDate(selectedDate) }}
            </div>
            <!-- <div class="form-day-sub">{{ weekdayLabel(selectedWeekday) }}</div> -->
          </div>
        </div>

        <b-form class="sidebar-form-modern" @submit.prevent="saveEntry()">
          <b-form-group
            :label="$t(getLabel('Class')) + ' *'"
            class="modern-form-group"
          >
            <v-select
              v-model="timetable.clsID"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="[...classesOptions, { id: 0, name: 'All' }]"
              :clearable="false"
              :reduce="(val) => val.id"
              label="name"
              :disabled="metaLoading"
              :placeholder="$t('Select ' + getLabel('class'))"
            />
            <!-- @input="fillSections()" -->
          </b-form-group>

          <b-form-group :label="$t('Subject') + ' *'" class="modern-form-group">
            <v-select
              v-model="timetable.subID"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="subjects"
              :reduce="(val) => val.id"
              label="subject"
              :clearable="false"
              :disabled="metaLoading"
              :placeholder="$t('Select subject')"
            />
          </b-form-group>

          <b-form-group
            :label="$t(getLabel('Section')) + ' *'"
            class="modern-form-group"
          >
            <v-select
              v-model="timetable.section"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="[{ id: 0, section: 'All' }, ...admittedSections]"
              :reduce="(val) => val.id"
              label="section"
              :clearable="false"
              :disabled="metaLoading"
              :placeholder="$t('Select ' + getLabel('section'))"
            />
          </b-form-group>

          <template v-if="isHappiness">
            <b-form-group :label="$t('Age Category')" class="modern-form-group">
              <v-select
                v-model="timetable.ageCategory"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="ageCategoryOptions"
                :reduce="(opt) => opt.title"
                label="title"
                :clearable="true"
                :placeholder="$t('Select age category')"
              />
            </b-form-group>
            <b-form-group
              :label="$t('Empower Level')"
              class="modern-form-group"
            >
              <v-select
                v-model="timetable.empowerLevel"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="empowerLevelOptions"
                :reduce="(opt) => opt.title"
                label="title"
                :clearable="true"
                :placeholder="$t('Select empower level')"
              />
            </b-form-group>
          </template>

          <b-row>
            <b-col cols="6">
              <b-form-group
                :label="$t('Start Time') + ' *'"
                class="modern-form-group"
              >
                <flat-pickr
                  v-model="timetable.startTimeValue"
                  class="form-control"
                  placeholder="9:00 AM"
                  :config="{
                    enableTime: true,
                    noCalendar: true,
                    altFormat: 'h:i K',
                    altInput: true,
                    dateFormat: 'Y-m-d\\TH:i:S',
                    defaultHour: 8,
                  }"
                />
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                :label="$t('End Time') + ' *'"
                class="modern-form-group"
              >
                <flat-pickr
                  v-model="timetable.endTimeValue"
                  class="form-control"
                  placeholder="10:00 AM"
                  :config="{
                    enableTime: true,
                    noCalendar: true,
                    altFormat: 'h:i K',
                    altInput: true,
                    dateFormat: 'Y-m-d\\TH:i:S',
                    defaultHour: 10,
                  }"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <div class="form-footer-actions">
            <b-button
              variant="outline-secondary"
              block
              class="mb-1"
              @click="closeFormPanel()"
            >
              {{ $t("Cancel") }}
            </b-button>
            <b-button
              v-if="
                (sidebarFormMode === 'add' && rights.add) ||
                (sidebarFormMode === 'edit' && rights.edit)
              "
              variant="primary"
              block
              :disabled="saving"
              type="submit"
            >
              <b-spinner v-if="saving" small type="grow" />
              <span v-else>
                {{
                  sidebarFormMode === "edit"
                    ? $t("Update Class")
                    : $t("Save Class")
                }}
              </span>
            </b-button>
          </div>
        </b-form>
      </div>
    </b-sidebar>

    <!-- Copy Timetable Modal -->
    <b-modal
      id="modal-copy-timetable"
      v-model="copyModalVisible"
      :title="$t('Copy Timetable')"
      centered
      :ok-title="$t('Save')"
      :cancel-title="$t('Cancel')"
      :busy="copying"
      @ok.prevent="saveCopy()"
      @hidden="closeCopyModal()"
    >
      <b-form-group :label="$t('Select Dates')">
        <flat-pickr
          v-model="copyDates"
          class="form-control"
          :config="multiConfig"
          :placeholder="$t('Select dates')"
        />
      </b-form-group>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel()">
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="
            copying ||
            !copyDates ||
            (Array.isArray(copyDates)
              ? copyDates.length === 0
              : !copyDates.trim())
          "
          @click="ok()"
        >
          <b-spinner v-if="copying" small type="grow" />
          <span v-else>{{ $t("Save") }}</span>
        </b-button>
      </template>
    </b-modal>

    <!-- Send Message Modal -->
    <b-modal
      id="modal-timetable-message"
      v-model="messageModalVisible"
      :title="$t('Send Message')"
      centered
      size="md"
      :busy="sendingMessage"
      @hidden="closeMessageModal()"
    >
      <b-form-group :label="$t('Message') + ' *'">
        <b-form-textarea
          v-model="messageText"
          :placeholder="$t('Type your message here...')"
          rows="4"
        />
      </b-form-group>
      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          :disabled="sendingMessage"
          @click="cancel()"
        >
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="sendingMessage || !messageText || !messageText.trim()"
          @click="sendMessage()"
        >
          <b-spinner v-if="sendingMessage" small type="grow" />
          <span v-else>{{ $t("Send") }}</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BRow,
  BSidebar,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { mapActions } from "vuex";

const WEEKDAYS = [
  { text: "Monday", value: "monday" },
  { text: "Tuesday", value: "tuesday" },
  { text: "Wednesday", value: "wednesday" },
  { text: "Thursday", value: "thursday" },
  { text: "Friday", value: "friday" },
  { text: "Saturday", value: "saturday" },
  { text: "Sunday", value: "sunday" },
];

export default {
  name: "StaffTimetableCalendar",
  components: {
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BModal,
    BRow,
    BSidebar,
    BSpinner,
    vSelect,
    flatPickr,
    FullCalendar,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      multiConfig: {
        mode: "multiple",
        dateFormat: "Y-m-d",
        altFormat: "j M, Y",
        altInput: true,
        disable: [],
        locale: {
          firstDayOfWeek: 1,
        },
      },
      copyModalVisible: false,
      copyDates: null,
      copying: false,
      copyingTimetableId: null,
      copyingSession: null,
      messageModalVisible: false,
      messageText: "",
      sendingMessage: false,
      messageTimetableId: null,
      messageSession: null,
      rights: {},
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      selectedStaffId: null,
      staffList: [],
      staffLoading: false,
      sidebarOverlayActive: false,
      loading: false,
      metaLoading: false,
      saving: false,
      formSidebarVisible: false,
      sidebarFormMode: "add",
      selectedDate: "",
      selectedWeekday: "",
      activeSessionKey: "",
      calendarRange: { start: null, end: null },
      eventsSignature: "",
      refreshingEvents: false,
      allEntries: [],
      daySessions: [],
      classes: [],
      classesOptions: [],
      admittedSections: [],
      subjects: [],
      timetable: {},
      myObj: {
        timetable: {},
        days: [],
      },
      ageCategoryOptions: [
        // { text: "Junior", value: "junior" },
        // { text: "Senior", value: "senior" },
      ],
      empowerLevelOptions: [
        // { text: "Beginner", value: "beginner" },
        // { text: "Intermediate", value: "intermediate" },
        // { text: "Advanced", value: "advanced" },
      ],
      calendarOptions: {
        plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin],
        initialView: "dayGridMonth",
        firstDay: 1,
        headerToolbar: {
          start: "sidebarToggle, prev,next, title",
          end: "dayGridMonth,timeGridWeek,listMonth",
        },
        customButtons: {
          sidebarToggle: {
            text: " ",
            click: () => {
              this.sidebarOverlayActive = !this.sidebarOverlayActive;
            },
          },
        },
        height: "auto",
        fixedWeekCount: false,
        dayMaxEventRows: 6,
        moreLinkClick: (info) => {
          if (info.date) {
            this.onDateClick({
              dateStr: this.formatLocalDate(info.date),
              date: info.date,
              jsEvent: info.jsEvent,
            });
          }
        },
        navLinks: false,
        events: [],
      },
    };
  },
  computed: {
    isHappiness() {
      return this.reportDomain === "myskoolhappinessfoundation";
    },
    staffId() {
      return parseInt(this.selectedStaffId) || 0;
    },
    staffOptions() {
      return this.staffList.map((staff) => ({
        ...staff,
        label: staff.designation
          ? `${staff.name} — ${staff.designation}`
          : staff.name,
      }));
    },
    selectedStaff() {
      return this.staffList.find((s) => s.id === this.selectedStaffId) || null;
    },
  },
  watch: {
    selectedStaffId(staffId, prev) {
      if (!staffId || staffId === prev) return;
      this.applyStaffChange(staffId);
    },
  },
  created() {
    // if (!this.$store.state.rights[this.$route.name]) {
    //   this.$router.replace({ name: "misc-not-authorized" });
    //   return;
    // }
    // this.rights = this.$store.state.rights[this.$route.name];
    // if (!this.rights.view) {
    //   this.$router.replace({ name: "misc-not-authorized" });
    //   return;
    // }
    // for dev
    this.rights = { view: true, edit: true, delete: true, add: true };

    this.timetable = this.emptyTimetable();
    this.calendarOptions.dateClick = (info) => this.onDateClick(info);
    this.calendarOptions.eventClick = (info) => this.onEventClick(info);
    this.calendarOptions.datesSet = (info) => this.onDatesSet(info);
    this.calendarOptions.eventOrder = (a, b) =>
      (a.extendedProps?.slotIndex ?? 0) - (b.extendedProps?.slotIndex ?? 0);
    this.calendarOptions.eventClassNames = (arg) => [
      "tt-cal-event",
      `tt-event-tone-${arg.event.extendedProps?.toneIndex || 0}`,
    ];
    // this.loadStaffList();
    this.applyStaffChange();
    this.loadMeta();
    if (this.isHappiness) this.loadHappinessDropdowns();
  },
  mounted() {
    this.syncCalendarRangeFromApi();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    syncCalendarRangeFromApi() {
      this.$nextTick(() => {
        const api = this.$refs.calendar?.getApi?.();
        if (!api?.view) return;
        const start = api.view.activeStart;
        const end = api.view.activeEnd;
        const startMs = start?.getTime?.();
        const endMs = end?.getTime?.();
        if (
          startMs === this.calendarRange.start?.getTime?.() &&
          endMs === this.calendarRange.end?.getTime?.()
        ) {
          this.refreshCalendarEvents();
          return;
        }
        this.onDatesSet({ start, end });
      });
    },

    sortEntriesByTime(entries) {
      return [...entries].sort(
        (a, b) =>
          this.getTimeValue(a.startTime) - this.getTimeValue(b.startTime)
      );
    },

    truncate(text, max = 48) {
      if (!text) return "";
      const t = String(text).trim();
      if (t.length <= max) return t;
      return `${t.slice(0, Math.max(0, max - 1))}…`;
    },

    emptyTimetable() {
      return {
        id: 0,
        tID: this.staffId,
        campusID: this.$store.state.userData.cId,
        day: "",
        clsID: 0,
        section: 0,
        subID: 0,
        startTime: "",
        endTime: "",
        dtFrom: null,
        dtTo: null,
        startTimeValue: null,
        endTimeValue: null,
        ageCategory: "",
        empowerLevel: "",
      };
    },

    getLabel(key) {
      const map = {
        class: this.isHappiness ? "group" : "class",
        classes: this.isHappiness ? "groups" : "classes",
        section: this.isHappiness ? "level" : "section",
        sections: this.isHappiness ? "levels" : "sections",
      };
      const result = map[key.toLowerCase()] ?? key.toLowerCase();
      const isCapitalized =
        key[0] === key[0].toUpperCase() && key[0] !== key[0].toLowerCase();
      return isCapitalized
        ? result.charAt(0).toUpperCase() + result.slice(1)
        : result;
    },

    weekdayLabel(value) {
      const match = WEEKDAYS.find((d) => d.value === value);
      return match ? match.text : value;
    },

    formatDisplayDate(dateStr) {
      if (!dateStr) return "";
      return new Date(`${dateStr}T12:00:00`).toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },

    getWeekdayKey(dateInput) {
      const date = new Date(dateInput);
      const names = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      return names[date.getDay()];
    },

    normalizeEntry(item, dayKey) {
      const obj = item.obj || item;
      const rawDay = obj.day || dayKey || "";
      const normalizedDay = String(rawDay).toLowerCase();
      let dateStr = "";
      if (obj.dtFrom) {
        dateStr = this.formatLocalDate(new Date(obj.dtFrom));
      }
      return {
        ...item,
        dayKey: normalizedDay || dayKey,
        dateStr,
        obj,
        name: item.name || obj.name || obj.className || "",
        section: item.section || obj.section || obj.sectionName || "",
        subject: item.subject || obj.subject || obj.subjectName || "",
        startTime: item.startTime || obj.startTime || "",
        endTime: item.endTime || obj.endTime || "",
        ageCategory: obj.ageCategory || item.ageCategory || "",
        empowerLevel: obj.empowerLevel || item.empowerLevel || "",
      };
    },

    sessionDisplayTitle(session) {
      return session.subject || this.$t("Class");
    },

    sessionDisplayMeta(session) {
      const classLabel = this.getLabel("Class");
      const sectionLabel = this.getLabel("Section");
      const parts = [];
      if (session.name) parts.push(`${classLabel}: ${session.name}`);
      if (session.section) parts.push(`${sectionLabel}: ${session.section}`);
      return parts.join(" · ") || "-";
    },

    sessionDisplayTime(session) {
      if (session.startTime && session.endTime) {
        return `${session.startTime} – ${session.endTime}`;
      }
      return session.startTime || session.endTime || "";
    },

    buildEventDisplay(entry) {
      const metaParts = [];
      if (entry.name) metaParts.push(entry.name);
      if (entry.section) metaParts.push(entry.section);

      return {
        title: entry.subject || this.$t("Class"),
        subtitle: metaParts.join(" · "),
        timeLabel: this.sessionDisplayTime(entry),
      };
    },

    selectSession(session) {
      this.activeSessionKey = this.sessionKey(session);
    },

    scrollToActiveSession() {
      this.$nextTick(() => {
        const root = this.$el.querySelector(".session-list");
        if (!root || !this.activeSessionKey) return;
        const el = root.querySelector(
          `[data-session-key="${this.activeSessionKey}"]`
        );
        if (el) {
          el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      });
    },

    openSidebarForDate(dateStr, weekday, entry) {
      // console.log(dateStr, weekday, entry);
      this.selectedDate = dateStr;
      this.selectedWeekday = weekday || this.getWeekdayKey(dateStr);
      this.refreshDaySessions();

      if (entry) {
        this.activeSessionKey = this.sessionKey(entry);
      } else if (this.daySessions.length === 1) {
        this.activeSessionKey = this.sessionKey(this.daySessions[0]);
      } else {
        this.activeSessionKey = "";
      }

      this.ensureSidebarOpen();
      this.scrollToActiveSession();
    },

    ensureSidebarOpen() {
      if (window.innerWidth < 992) {
        this.sidebarOverlayActive = true;
      }
    },

    sessionKey(session) {
      const obj = session.obj || {};
      return `${obj.id || "new"}-${session.dayKey}-${session.startTime}`;
    },

    formatLocalDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    async loadStaffList() {
      this.staffLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "staff/filter?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dptID=0&status=present",
          token: this.$store.state.userData.token,
        });
        this.staffList = Array.isArray(res) ? res : [];
        if (!this.selectedStaffId && this.staffList.length) {
          this.selectedStaffId = this.staffList[0].id;
        }
      } finally {
        this.staffLoading = false;
      }
    },

    applyStaffChange() {
      this.timetable = this.emptyTimetable();
      this.allEntries = [];
      this.daySessions = [];
      this.selectedDate = "";
      this.selectedWeekday = "";
      this.activeSessionKey = "";
      this.formSidebarVisible = false;
      this.eventsSignature = "";
      this.refreshCalendarEvents();
      this.reloadAll();
    },

    async loadMeta() {
      this.metaLoading = true;
      try {
        const classes = await this.get({
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        this.classes = classes || [];
        this.classesOptions = this.classes.map((el) => el.cls);

        var obj = {
          url:
            this.$store.state.domain +
            "Sections?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        this.admittedSections = await this.get(obj);

        this.subjects = await this.get({
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
      } finally {
        this.metaLoading = false;
      }
    },
    async loadHappinessDropdowns() {
      const [item1, item2] = await Promise.all([
        this.loadDropdownList("ageCategory"),
        this.loadDropdownList("empowerLevel"),
      ]);
      this.ageCategoryOptions = item1 || [];
      this.empowerLevelOptions = item2 || [];
    },
    async loadDropdownList(category) {
      var obj = {
        url:
          this.$store.state.domain +
          "DropdownList/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&category=" +
          category,
        token: this.$store.state.userData.token,
      };
      const res = await this.get(obj);
      return Array.isArray(res) ? res : [];
    },

    async reloadAll() {
      this.loading = true;
      try {
        const response = await this.get({
          url:
            this.$store.state.domain +
            "TimeTable/LoadCalendarView?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });

        const items = response || [];
        const merged = [];
        items.forEach((item) => {
          const obj = item.obj || item;
          let dayKey = "";
          if (obj.dtFrom) {
            dayKey = this.getWeekdayKey(new Date(obj.dtFrom));
          }
          merged.push(this.normalizeEntry(item, dayKey));
        });
        this.allEntries = merged;
        this.refreshDaySessions();
        this.eventsSignature = "";
        this.$nextTick(() => {
          if (!this.calendarRange.start) {
            this.syncCalendarRangeFromApi();
          } else {
            this.refreshCalendarEvents();
          }
        });
      } finally {
        this.loading = false;
      }
    },

    onDatesSet(info) {
      const startMs = info.start?.getTime?.();
      const endMs = info.end?.getTime?.();
      const prevStart = this.calendarRange.start?.getTime?.();
      const prevEnd = this.calendarRange.end?.getTime?.();

      if (startMs === prevStart && endMs === prevEnd) return;

      this.calendarRange = {
        start: info.start,
        end: info.end,
      };
      this.eventsSignature = "";
      this.refreshCalendarEvents();
    },

    buildCalendarEvents() {
      if (!this.calendarRange.start || !this.calendarRange.end) {
        return [];
      }

      const byDate = {};
      this.allEntries.forEach((entry) => {
        const dStr = entry.dateStr;
        if (dStr) {
          if (!byDate[dStr]) byDate[dStr] = [];
          byDate[dStr].push(entry);
        }
      });
      Object.keys(byDate).forEach((key) => {
        byDate[key] = this.sortEntriesByTime(byDate[key]);
      });

      const events = [];
      const cursor = new Date(this.calendarRange.start);
      cursor.setHours(12, 0, 0, 0);
      const end = new Date(this.calendarRange.end);
      end.setHours(12, 0, 0, 0);

      while (cursor < end) {
        const dateStr = this.formatLocalDate(cursor);
        const weekday = this.getWeekdayKey(cursor);
        const dayEntries = byDate[dateStr] || [];

        dayEntries.forEach((entry, slotIndex) => {
          const display = this.buildEventDisplay(entry);
          const entryId = entry.obj?.id || `x-${entry.startTime}-${slotIndex}`;
          events.push({
            id: `${entryId}-${dateStr}-${slotIndex}`,
            title: display.title,
            start: dateStr,
            allDay: true,
            extendedProps: {
              entry,
              date: dateStr,
              weekday,
              subtitle: display.subtitle,
              timeLabel: display.timeLabel,
              toneIndex: slotIndex % 4,
              slotIndex,
            },
          });
        });

        cursor.setDate(cursor.getDate() + 1);
      }

      return events;
    },

    refreshCalendarEvents() {
      if (this.refreshingEvents) return;

      const events = this.buildCalendarEvents();
      const signature = [
        this.staffId,
        events.length,
        this.calendarRange.start?.getTime?.() || 0,
        this.calendarRange.end?.getTime?.() || 0,
        events.map((e) => e.id).join(","),
      ].join("|");

      if (signature === this.eventsSignature) return;

      this.eventsSignature = signature;
      this.setCalendarEvents(events);
    },

    setCalendarEvents(events) {
      const api = this.$refs.calendar?.getApi?.();
      if (!api) {
        this.calendarOptions.events = events;
        return;
      }

      this.refreshingEvents = true;
      api.removeAllEvents();
      events.forEach((event) => api.addEvent(event));
      this.$nextTick(() => {
        this.refreshingEvents = false;
      });
    },

    onDateClick(info) {
      // if (!this.staffId) {
      //   this.$bvToast.toast(this.$t("Please select a staff member first."), {
      //     title: this.$t("Staff Required"),
      //     variant: "warning",
      //     toaster: "b-toaster-top-center",
      //   });
      //   return;
      // }
      if (info.jsEvent) {
        info.jsEvent.preventDefault();
      }
      this.openSidebarForDate(info.dateStr, this.getWeekdayKey(info.date));
    },

    onEventClick(info) {
      // if (!this.staffId) return;
      if (info.jsEvent) {
        info.jsEvent.preventDefault();
      }
      const props = info.event.extendedProps || {};
      const date = props.date || info.event.startStr?.slice(0, 10);
      if (!date) return;
      const weekday = props.weekday || this.getWeekdayKey(`${date}T12:00:00`);
      this.openSidebarForDate(date, weekday, props.entry);
      if (props.entry && this.rights.edit) {
        this.$nextTick(() => this.openEditForm(props.entry));
      }
    },

    onSessionClick(session) {
      if (!this.rights.edit) return;
      this.openEditForm(session);
    },
    getTimeValue(time) {
      if (!time) return 0;

      const match = String(time)
        .trim()
        .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);

      if (!match) return 0;

      let hours = Number(match[1]);
      const minutes = Number(match[2]);
      const period = match[3]?.toUpperCase();

      if (period) {
        if (hours === 12) hours = 0;
        if (period === "PM") hours += 12;
      }

      return hours * 60 + minutes;
    },
    refreshDaySessions() {
      if (!this.selectedDate) {
        this.daySessions = [];
        return;
      }
      // .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));
      this.daySessions = this.allEntries
        .filter((entry) => entry.dateStr === this.selectedDate)
        .sort(
          (a, b) =>
            this.getTimeValue(a.startTime) - this.getTimeValue(b.startTime)
        );
    },

    fillSections() {
      this.admittedSections = [];
      this.classes.forEach((elem) => {
        if (elem.cls.id === this.timetable.clsID) {
          this.admittedSections = elem.sections || [];
        }
      });
    },

    openMessageModal(session) {
      const obj = session.obj || session || {};
      if (!obj.id) return;
      this.messageSession = session;
      this.messageTimetableId = obj.id;
      this.messageText = "";
      this.messageModalVisible = true;
    },

    closeMessageModal() {
      this.messageModalVisible = false;
      this.messageText = "";
      this.messageTimetableId = null;
      this.messageSession = null;
      this.sendingMessage = false;
    },

    async sendMessage() {
      if (
        !this.messageTimetableId ||
        !this.messageText ||
        !this.messageText.trim()
      ) {
        return this.$bvToast.toast(this.$t("Please enter a message."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }

      this.sendingMessage = true;
      try {
        const body = {
          timetableID: this.messageTimetableId,
          message: this.messageText.trim(),
          userID: this.$store.state.userData.userID,
        };

        const ok = await this.post({
          url:
            this.$store.state.domain +
            "timetable/SendMessage?db=" +
            this.$store.state.userData.db,
          body,
          message: this.$t("Message sent successfully."),
          context: this,
          token: this.$store.state.userData.token,
        });

        if (ok) {
          this.closeMessageModal();
        }
      } catch (error) {
        console.error("Send message error:", error);
        this.$bvToast.toast(this.$t("Failed to send message."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.sendingMessage = false;
      }
    },

    openCopyForm(session) {
      const obj = session.obj || session || {};
      if (!obj.id) return;
      this.copyingSession = session;
      this.copyingTimetableId = obj.id;
      this.copyDates = null;
      const currentDtFrom =
        session.dateStr ||
        (obj.dtFrom ? this.formatLocalDate(new Date(obj.dtFrom)) : "");
      this.multiConfig = {
        ...this.multiConfig,
        disable: currentDtFrom ? [currentDtFrom] : [],
      };
      this.copyModalVisible = true;
    },

    closeCopyModal() {
      this.copyModalVisible = false;
      this.copyDates = null;
      this.copyingTimetableId = null;
      this.copyingSession = null;
      this.multiConfig = {
        ...this.multiConfig,
        disable: [],
      };
    },

    async saveCopy() {
      let datesArray = [];

      if (Array.isArray(this.copyDates)) {
        datesArray = this.copyDates;
      } else if (typeof this.copyDates === "string") {
        datesArray = this.copyDates
          .split(",")
          .map((d) => d.trim())
          .filter(Boolean);
      }
      // console.log(datesArray)

      if (!datesArray || datesArray.length === 0) {
        return this.$bvToast.toast(
          this.$t("Please select at least one date."),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      }

      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t(
          "Do you want to copy this timetable to the selected dates?"
        ),
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: this.$t("Yes, Copy it!"),
      });

      if (!result.isConfirmed) return;

      this.copying = true;
      try {
        const ok = await this.post({
          url:
            this.$store.state.domain +
            "TimeTable/CopyTimetable?db=" +
            this.$store.state.userData.db +
            "&timetableID=" +
            this.copyingTimetableId,
          body: datesArray,
          message: this.$t("Time Table copied successfully."),
          context: this,
          token: this.$store.state.userData.token,
        });

        if (ok) {
          this.copyModalVisible = false;
          await this.reloadAll();
        }
      } finally {
        this.copying = false;
      }
    },

    openAddForm() {
      if (!this.rights.add || !this.selectedWeekday) return;
      this.sidebarFormMode = "add";
      this.timetable = {
        ...this.emptyTimetable(),
        day: this.selectedWeekday,
        dtFrom: this.selectedDate,
        dtTo: this.selectedDate,
      };
      this.myObj.days = [this.selectedWeekday];
      // this.admittedSections = [];
      this.formSidebarVisible = true;
    },

    openEditForm(session) {
      if (!this.rights.edit) return;
      const obj = { ...(session.obj || {}) };
      this.sidebarFormMode = "edit";
      this.activeSessionKey = this.sessionKey(session);
      this.timetable = {
        ...this.emptyTimetable(),
        ...obj,
        ageCategory: obj.ageCategory || session.ageCategory || "",
        empowerLevel: obj.empowerLevel || session.empowerLevel || "",
      };
      // this.fillSections();
      this.formSidebarVisible = true;
    },

    closeFormPanel() {
      this.formSidebarVisible = false;
      this.timetable = {
        ...this.emptyTimetable(),
        day: this.selectedWeekday,
      };
    },

    deleteCurrentEntry() {
      if (!this.rights.delete) return;
      if (!this.timetable.id) return;
      const session = this.daySessions.find(
        (s) => (s.obj || {}).id === this.timetable.id
      ) || {
        obj: { id: this.timetable.id, day: this.timetable.day },
        dayKey: this.selectedWeekday,
      };
      this.deleteEntry(session);
    },

    validateForm() {
      if (
        // !this.timetable.clsID ||
        // !this.timetable.section ||
        !this.timetable.subID ||
        !this.timetable.startTimeValue ||
        !this.timetable.endTimeValue ||
        !this.timetable.day
      ) {
        this.$bvToast.toast(this.$t("Please fill the form correctly."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return false;
      }
      return true;
    },

    async saveEntry() {
      if (this.timetable.id && !this.rights.edit) return;
      if (!this.timetable.id && !this.rights.add) return;
      if (!this.validateForm()) return;

      this.timetable.tID = this.staffId;
      // console.log(this.timetable);
      this.saving = true;
      try {
        let ok = false;

        ok = await this.post({
          url:
            this.$store.state.domain +
            "TimeTable/SaveCalendarView?db=" +
            this.$store.state.userData.db,
          body: this.timetable,
          message: "Time Table saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });

        // if (this.timetable.id) {
        //   ok = await this.put({
        //     url:
        //       this.$store.state.domain +
        //       "TimeTable/" +
        //       this.timetable.id +
        //       "?db=" +
        //       this.$store.state.userData.db,
        //     body: this.timetable,
        //     message: "Time Table updated successfully.",
        //     context: this,
        //     token: this.$store.state.userData.token,
        //   });
        // } else {
        //   this.myObj.timetable = this.timetable;
        //   this.myObj.days = [this.timetable.day];
        //   ok = await this.post({
        //     url:
        //       this.$store.state.domain +
        //       "TimeTable/save?db=" +
        //       this.$store.state.userData.db,
        //     body: this.myObj,
        //     message: "Time Table added successfully.",
        //     context: this,
        //     token: this.$store.state.userData.token,
        //   });
        // }

        if (ok) {
          this.formSidebarVisible = false;
          await this.reloadAll();
          // this.refreshDaySessions();
        }
      } finally {
        this.saving = false;
      }
    },

    async deleteEntry(session) {
      if (!this.rights.delete) return;
      const obj = session.obj || {};
      if (!obj.id) return;

      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (!result.isConfirmed) return;

      const ok = await this.delete({
        url:
          this.$store.state.domain +
          "TimeTable/" +
          obj.id +
          "?db=" +
          this.$store.state.userData.db,
        body: null,
        message: "Time Table removed successfully.",
        context: this,
        token: this.$store.state.userData.token,
      });

      if (ok) {
        this.formSidebarVisible = false;
        await this.reloadAll();
        // this.refreshDaySessions();
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/apps/calendar.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

// --- CALENDAR SKELETON & LOADING ---
.calendar-skeleton-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  z-index: 50;
  display: flex;
  flex-direction: column;

  .skeleton-header {
    height: 70px;
    align-items: center;
  }

  .skeleton-title-box {
    width: 200px;
    height: 32px;
    background: #f1f5f9;
    border-radius: 8px;
    animation: tt-shimmer 1.5s infinite linear;
  }

  .skeleton-btn {
    width: 40px;
    height: 40px;
    background: #f1f5f9;
    border-radius: 12px;
    animation: tt-shimmer 1.5s infinite linear;
  }

  .skeleton-row {
    width: 100%;
  }

  .skeleton-cell-header {
    flex: 1;
    height: 20px;
    background: #f8fafc;
    margin: 0 4px;
    border-radius: 4px;
    animation: tt-shimmer 1.5s infinite linear;
  }

  .skeleton-cell {
    flex: 1;
    height: 110px;
    border: 1px solid #f8fafc;
    padding: 10px;
    background: #fff;

    .skeleton-date {
      width: 24px;
      height: 24px;
      background: #f1f5f9;
      border-radius: 6px;
      margin-bottom: 20px;
      margin-left: auto;
      animation: tt-shimmer 1.5s infinite linear;
    }

    .skeleton-event-bar {
      height: 40px;
      background: #f1f5f9;
      border-radius: 10px;
      width: 100%;
      animation: tt-shimmer 1.5s infinite linear;
    }

    .skeleton-event-bar-small {
      height: 35px;
      background: #f8fafc;
      border-radius: 10px;
      width: 85%;
      animation: tt-shimmer 1.5s infinite linear;
    }
  }
}

.calendar-loading-blur {
  filter: blur(2px);
  opacity: 0.6;
  pointer-events: none;
  transition: all 0.3s ease;
}

@keyframes tt-shimmer {
  0% {
    background: #f1f5f9;
  }
  50% {
    background: #e2e8f0;
  }
  100% {
    background: #f1f5f9;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// --- SIDEBAR TOGGLE ICON ---
.fc-sidebarToggle-button {
  padding: 0 !important;
  width: 40px !important;
  height: 40px !important;
  display: none !important;
  align-items: center;
  justify-content: center;
  background: #f8fafc !important;
  border: 1px solid #f1f5f9 !important;
  position: relative;

  @media (max-width: 991px) {
    display: flex !important;
  }

  &::after {
    content: "";
    display: block;
    width: 18px;
    height: 18px;
    background-color: #7367f0;
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='3' y1='12' x2='21' y2='12'%3E%3C/line%3E%3Cline x1='3' y1='6' x2='21' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='18' x2='21' y2='18'%3E%3C/line%3E%3C/svg%3E");
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
  }

  &:hover {
    background: #fff !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }
}

// --- CORPORATE CALENDAR THEME (matches event-calendar) ---
.app-calendar {
  border-radius: 20px !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.04);
  background: #fff;
  border: 1px solid #f1f5f9 !important;
  overflow: hidden;

  .app-calendar-sidebar {
    background: #ffffff;
    border-right: 1px solid #f1f5f9;
    padding: 0;
    min-width: 280px;
    z-index: 10;

    &.show {
      z-index: 11;
      box-shadow: 8px 0 32px rgba(15, 23, 42, 0.12);
    }
  }
}

.body-content-overlay {
  z-index: 10 !important;
}

.sidebar-wrapper {
  background: #ffffff;
  height: 100%;
}

.sidebar-page-title {
  display: flex;
  align-items: center;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -0.02em;
}

.sidebar-page-hint {
  font-size: 0.72rem;
  color: #94a3b8;
  line-height: 1.4;
}

.sidebar-card {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 0.85rem 1rem;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.03);
}

.sidebar-card-day {
  background: linear-gradient(135deg, #f8f9ff 0%, #fff 100%);
  border-color: rgba(115, 103, 240, 0.15);
}

.sidebar-card-form {
  padding-bottom: 0.5rem;
}

.sidebar-card-label {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 0.5rem;
}

.classes-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.85rem;
}

.classes-count {
  background: #f0f1ff;
  color: #7367f0;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 20px;
}

.staff-vselect .vs__dropdown-toggle {
  border-radius: 10px;
  border-color: #eef2f7;
  min-height: 40px;
}

.staff-chip {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  background: #f8f9ff;
  border-radius: 10px;
  border: 1px solid rgba(115, 103, 240, 0.1);
}

.staff-chip-avatar {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #7367f0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.staff-chip-name {
  font-weight: 700;
  font-size: 0.88rem;
  color: #1e293b;
  line-height: 1.2;
}

.staff-chip-meta {
  font-size: 0.72rem;
  color: #94a3b8;
}

.sidebar-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1rem;
  color: #94a3b8;
  font-size: 0.82rem;
  line-height: 1.5;
}

.sidebar-empty-compact {
  padding: 1.25rem 0.75rem;
}

.sidebar-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.82rem;
  font-weight: 700;
  color: #7367f0;
  cursor: pointer;

  &:hover {
    color: #5e50ee;
  }
}

.sidebar-field {
  margin-bottom: 0.85rem;

  legend,
  label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #475569;
    margin-bottom: 0.35rem;
  }
}

.sidebar-form-actions {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f1f5f9;
}

.session-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 2px;
}

.session-card {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(115, 103, 240, 0.25);
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
  }

  &.active {
    border-color: #7367f0;
    background: #faf9ff;
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.12);
  }

  &--readonly {
    cursor: default;

    &:hover {
      border-color: #eef2f7;
      box-shadow: none;
    }
  }
}

.session-card-main {
  flex: 1;
  min-width: 0;
}

.session-card-time {
  font-size: 0.68rem;
  font-weight: 800;
  color: #7367f0;
  letter-spacing: 0.02em;
  margin-bottom: 0.15rem;
}

.session-card-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.25;
  margin-bottom: 0.15rem;
}

.session-card-meta {
  font-size: 0.72rem;
  color: #94a3b8;
  line-height: 1.35;
}

.session-card-actions {
  display: flex;
  flex-shrink: 0;
  gap: 0.1rem;
}

.add-class-link-btn {
  display: none;
}

.session-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.35rem;
}

.session-tag {
  font-size: 0.62rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  background: #f0f1ff;
  color: #7367f0;
}

.selected-day-title {
  font-weight: 800;
  color: #1e293b;
  font-size: 0.95rem;
  line-height: 1.3;
}

.selected-day-sub {
  font-size: 0.78rem;
  color: #7367f0;
  font-weight: 600;
  margin-top: 0.15rem;
}

.staff-option-name {
  font-weight: 600;
}

.add-event-premium-btn {
  display: none;
}

.full-calendar {
  padding: 1.5rem !important;

  .fc-header-toolbar {
    margin-bottom: 2.5rem !important;
    align-items: center;

    .fc-toolbar-title {
      font-size: 1.75rem !important;
      font-weight: 800 !important;
      color: #1e293b !important;
      letter-spacing: -0.5px;
      margin-left: 1rem;
    }

    .fc-button-group {
      background: #f8fafc;
      padding: 4px;
      border-radius: 12px;
      border: 1px solid #f1f5f9;

      .fc-button {
        background: transparent !important;
        border: none !important;
        color: #64748b !important;
        padding: 0.5rem 1rem !important;
        font-weight: 700 !important;
        font-size: 0.85rem !important;
        border-radius: 10px !important;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        text-transform: capitalize;

        &:hover {
          color: #1e293b !important;
          background: #fff !important;
        }

        &.fc-button-active {
          background: #ffffff !important;
          color: #7367f0 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
        }
      }
    }

    .fc-prev-button,
    .fc-next-button {
      background: #fff !important;
      border: 1px solid #f1f5f9 !important;
      color: #64748b !important;
      border-radius: 50% !important;
      padding: 0.4rem !important;
      margin: 0 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

      &:hover {
        border-color: #cbd5e1 !important;
        color: #1e293b !important;
        transform: translateY(-1px);
      }
    }
  }

  .fc-theme-standard {
    td,
    th {
      border: 1px solid #f1f5f9 !important;
    }

    .fc-col-header-cell {
      padding: 1.25rem 0;
      background: #fff;
      border-bottom: 2px solid #f1f5f9 !important;

      .fc-col-header-cell-cushion {
        color: #94a3b8;
        font-weight: 800;
        text-transform: uppercase;
        font-size: 0.7rem;
        letter-spacing: 1.5px;
      }
    }
  }

  .fc-daygrid-day {
    min-height: 120px !important;

    &:hover {
      background: #fcfcfd !important;
    }

    &.fc-day-today {
      background: #f8faff !important;

      .fc-daygrid-day-number {
        background: linear-gradient(135deg, #7367f0, #9d7aff);
        color: #fff !important;
        box-shadow: 0 4px 10px rgba(115, 103, 240, 0.35);
      }
    }

    .fc-daygrid-day-top {
      padding: 10px;
    }

    .fc-daygrid-day-number {
      font-weight: 700;
      color: #64748b;
      font-size: 0.95rem;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      transition: all 0.2s;
    }
  }

  .fc-event {
    background: transparent !important;
    border: none !important;
    margin: 3px 6px !important;
    padding: 0 !important;
    border-radius: 10px !important;
    box-shadow: none !important;
    transition: all 0.2s ease;
    overflow: visible !important;

    &:hover {
      transform: translateY(-1px);
      z-index: 5 !important;
    }
  }

  .fc-daygrid-event-harness {
    margin-bottom: 4px;
  }

  .fc-daygrid-day-events {
    margin-top: 2px;
  }

  .fc-more-link {
    font-size: 0.72rem;
    font-weight: 700;
    color: #7367f0 !important;
    margin: 2px 8px;
  }
}

.tt-event-block {
  padding: 6px 8px;
  border-radius: 10px;
  border-left: 4px solid #7367f0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  min-height: 48px;
  display: flex !important;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;

  &.tt-event-tone-0 {
    border-left-color: #7367f0;
    background: #faf9ff;
  }
  &.tt-event-tone-1 {
    border-left-color: #00cfe8;
    background: #f0fcff;
  }
  &.tt-event-tone-2 {
    border-left-color: #28c76f;
    background: #f3fcf6;
  }
  &.tt-event-tone-3 {
    border-left-color: #ff9f43;
    background: #fffaf3;
  }

  .event-time-badge {
    font-size: 0.62rem;
    font-weight: 800;
    color: #7367f0;
    letter-spacing: 0.02em;
    margin-bottom: 2px;
    line-height: 1.2;
  }

  .event-title {
    font-size: 0.78rem;
    line-height: 1.25;
    color: #1e293b;
    font-weight: 700;
    margin-bottom: 1px;
  }

  .event-meta-line {
    font-size: 0.62rem;
    color: #64748b;
    font-weight: 600;
    line-height: 1.2;
  }
}

.event-content {
  overflow: hidden;
}

.fc-event-students .event-content {
  border-left-color: #7367f0;
}

// --- RIGHT FORM SIDEBAR (event-calendar style) ---
.timetable-form-sidebar {
  .b-sidebar-body {
    padding: 0 !important;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.sidebar-header-modern {
  background: #ffffff;
  border-bottom: 2px solid #e0d8ff;
  position: sticky;
  top: 0;
  z-index: 100;
  padding-top: 4px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .sidebar-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
  }

  .sidebar-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .sidebar-title-icon-box {
    width: 40px;
    height: 40px;
    background: rgba(124, 93, 250, 0.08);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7c5dfa;
  }

  .sidebar-title {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
    margin: 0;
  }
}

.sidebar-body-modern {
  padding: 1.5rem;
  background: #ffffff;
  flex: 1;
  overflow-y: auto;
}

.form-day-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  background: #f8f9ff;
  border: 1px solid rgba(115, 103, 240, 0.15);
  border-radius: 12px;
}

.form-day-title {
  font-weight: 700;
  color: #1e293b;
  font-size: 0.9rem;
}

.form-day-sub {
  font-size: 0.75rem;
  color: #7367f0;
  font-weight: 600;
}

.sidebar-form-modern {
  .modern-form-group {
    margin-bottom: 1.25rem;

    label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #475569;
      margin-bottom: 0.5rem;
    }
  }

  .modern-input {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    min-height: 44px;

    &:focus {
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    }
  }

  .vs__dropdown-toggle {
    border-radius: 10px;
    border-color: #e0d8ff;
    min-height: 44px;
  }
}

.form-footer-actions {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #f1f5f9;
}
</style>
